import * as R from 'ramda'
import React, { useEffect, useRef, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form/dist/index.ie11'
import { useSelector } from 'react-redux'
import TimeField from 'react-simple-timefield'
import styled, { css } from 'styled-components'

import {
  ErrorLabel,
  FieldComponent,
  HelpText,
  Input,
  InputWrapper,
  OutsideLabel as Label
} from '../../components/Form/shared'
import { usePrev } from '../../hooks'
import { getColor, getFontSize } from '../../utils/cssUtils'
import { isExist } from '../../utils/ramdaUtils'

const toggleStyle = css`
  opacity: ${({ hide }) => (hide ? 0 : 1)};
  pointer-events: ${({ hide }) => (hide ? 'none' : 'auto')};
`
const StyledInput = styled(Input)`
  position: relative;
  z-index: 0;
  ${toggleStyle}
`
const EmptyInput = styled(Input)`
  position: absolute;
  z-index: 1;
  width: 100%;
  top: 0;
  left: 0;
  ${toggleStyle}
`
const NativeTimeInput = styled.input.attrs({
  type: 'time'
})`
  position: relative;
  appearance: none;
  display: inline-flex;
  width: 100%;
  height: 50px;
  border: none;
  background-color: ${getColor('input', 0.04)};
  padding: 8px 8px 8px 8px;
  font-size: ${getFontSize('body2')};
  color: ${getColor('black')};
  resize: none;
  outline: none;
  ${toggleStyle}
  &::-webkit-calendar-picker-indicator {
    width: 16px;
    height: 16px;
    display: block;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='%23b5b5b5' viewBox='0 0 24 24'%3E%3Cg id='icon-clock' transform='translate(-64 -64)'%3E%3Cpath id='Path_1' d='M76,64A12,12,0,1,0,88,76,12,12,0,0,0,76,64Zm0,21.964A9.964,9.964,0,1,1,85.964,76,9.966,9.966,0,0,1,76,85.964Z' data-name='Path 1'/%3E%3Cpath id='Path_2' d='M486.161,290.688l-4.25-3.073v-7.377a.239.239,0,0,0-.238-.238h-1.434a.239.239,0,0,0-.238.238v8.208a.236.236,0,0,0,.1.194l4.93,3.594a.241.241,0,0,0,.334-.051l.852-1.162A.238.238,0,0,0,486.161,290.688Z' data-name='Path 2' transform='translate(-405.242 -210.769)'/%3E%3C/g%3E%3C/svg%3E");
    cursor: pointer;
  }
`

const timeRegex = new RegExp(/^\d\d:\d\d/)

const TimeInput = ({
  name,
  label,
  placeholder,
  validationRules,
  helpText,
  showError = true
}) => {
  const { register, unregister, setValue, errors, trigger } = useFormContext()

  const isAppview = useSelector(R.path(['appState', 'isAppview']))

  const formValue = useWatch({ name, defaultValue: '' })

  const [timeValueLocalState, setTimeValueLocalState] = useState('23:59')

  const timeFieldRef = useRef()

  const changeCount = useRef(0)

  const timeFieldSelectionRef = useRef('')

  const [shouldFocus, setShouldFocus] = useState(false)

  const [show, setShow] = useState(false)

  const prevFormValue = usePrev(formValue)

  const handleInputFocus = e => {
    setShow(true)
    setShouldFocus(true)
  }

  const handleNativeTimeInputChange = e => {
    setTimeValueLocalState(e.target.value)
  }

  const onTimeChange = (e, time) => {
    setTimeValueLocalState(time)
    if (timeValueLocalState !== e.target.value) {
      changeCount.current += 1
    }
  }

  const handleNativeTimeInputBlur = e => {
    if (R.isEmpty(e.target.value)) {
      setShow(false)
    } else {
      setValue(name, e.target.value, { shouldDirty: true })
    }
  }

  const handleTimeFieldBlur = e => {
    setValue(name, timeValueLocalState, { shouldDirty: true })
    trigger(name)
  }

  const handleTimeFieldSelect = e => {
    const selected = timeFieldRef.current.value.substring(
      timeFieldRef.current.selectionStart,
      timeFieldRef.current.selectionEnd
    )
    timeFieldSelectionRef.current = selected
  }

  const handleTimeFieldKeyUp = e => {
    if (e.keyCode === 8 || e.keyCode === 46) {
      if (R.test(timeRegex, timeFieldSelectionRef.current)) {
        e.stopPropagation()
        changeCount.current = 0
        timeFieldSelectionRef.current = ''
      } else if (e.target.value === '00:00') {
        changeCount.current = 0
      }
    }
    if (
      e.keyCode === 48 &&
      R.length(R.filter(R.equals('0'), timeValueLocalState)) === 4
    ) {
      changeCount.current = 1
    }
  }

  const hasError = !R.isNil(errors[name])

  useEffect(() => {
    if (show && timeFieldRef.current) {
      shouldFocus && timeFieldRef.current.focus()
      if (timeFieldRef.current.type !== 'time') {
        timeFieldRef.current.selectionStart = 0
        timeFieldRef.current.selectionEnd = 2
      }
    } else {
      setShouldFocus(false)
    }
  }, [show, shouldFocus])

  useEffect(() => {
    register({ name }, validationRules)
    return () => unregister(name)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, name])

  useEffect(() => {
    // react-hook-form cache the validationRules, therefore need to register again when the rules change
    register({ name }, validationRules)
    setValue(name, formValue, { shouldDirty: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationRules])

  // populate default value from form
  useEffect(() => {
    if (
      isExist(formValue) &&
      R.test(timeRegex, formValue) &&
      R.isEmpty(prevFormValue)
    ) {
      setShow(true)
      setShouldFocus(false)
      setTimeValueLocalState(formValue)
      changeCount.current += 1
    }
  }, [formValue, prevFormValue, name])

  return (
    <>
      {label && <Label>{label}</Label>}
      <FieldComponent>
        <InputWrapper hasError={hasError} value={timeValueLocalState}>
          {isAppview ? (
            <NativeTimeInput
              ref={timeFieldRef}
              onChange={handleNativeTimeInputChange}
              onBlur={handleNativeTimeInputBlur}
              hide={!show}
              value={timeValueLocalState}
              tabIndex={show ? 0 : -1}
            />
          ) : (
            <TimeField
              value={timeValueLocalState}
              input={
                <StyledInput
                  ref={timeFieldRef}
                  onBlur={handleTimeFieldBlur}
                  onSelect={handleTimeFieldSelect}
                  onKeyUp={handleTimeFieldKeyUp}
                  type='text'
                  inputMode='numeric'
                  hide={!show}
                  tabIndex={show ? 0 : -1}
                />
              }
              onChange={onTimeChange}
            />
          )}
          <EmptyInput
            hide={show}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            readOnly
            tabIndex={show ? -1 : 0}
          />
        </InputWrapper>
        {helpText && <HelpText>{helpText}</HelpText>}
        {showError && hasError && (
          <ErrorLabel>{R.path([name, 'message'], errors)}</ErrorLabel>
        )}
      </FieldComponent>
    </>
  )
}

export default TimeInput
